import {
  ArrowLeftOnRectangleIcon,
  BellAlertIcon,
  BellIcon,
  BoltIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  Cog6ToothIcon,
  EllipsisHorizontalIcon,
  RectangleStackIcon,
  RocketLaunchIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  CaretSortIcon,
  RadiobuttonIcon,
  StopwatchIcon,
  TriangleRightIcon,
} from "@radix-ui/react-icons";
import * as Popover from "@radix-ui/react-popover";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetInstance } from "src/api/General/instance";
import { useGetSettings } from "src/api/General/settings";
import {
  useGetPendingInternalUpdatesForUser,
  useGetPhase,
} from "src/api/Services/SharedSpace/phases";
import { useGetSharedSpacesForUser } from "src/api/Services/SharedSpace/spaces";
import { SpaceData, useGetSpaces } from "src/api/Spaces/spaces";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { useRealmApp } from "src/store/RealmApp";
import { getFirstAndLastInitials } from "src/utils/functions/avatar";
import { classNames } from "src/utils/functions/classes";
import { Button } from "../ui/button";
// import { Notifications } from "./TailwindSidebar";
import { ObjectID } from "bson";
import dayjs from "dayjs";
import {
  NotificationData,
  useGetAllNotifications,
  useGetNotificationsForSharedSpaceForUser,
  useGetUnreadNotifications,
  useGetUnreadNotificationsForSharedSpaceForUser,
  useMarkNotificationAsRead,
  useMarkSharedSpaceNotificationAsRead,
} from "src/api/General/notifications";
import { Count } from "../ui/count";
type Props = {};
// type Workspace = { type: "instance" | "shared-space"; id: string };

export default function Sidebar({}: Props) {
  const location = useLocation();
  const { data: spaces } = useGetSpaces();
  const { data: settings } = useGetSettings();
  const { slug } = useParams();
  const { sharedSpaces } = useGetSharedSpacesForUser();
  const app = useRealmApp();
  const [spacesOpen, setSpacesOpen] = useState(
    !!localStorage.getItem("spacesOpen")
      ? localStorage.getItem("spacesOpen") === "true"
      : true
  );
  useEffect(() => {
    localStorage.setItem("spacesOpen", String(spacesOpen));
  }, [spacesOpen]);

  const [workspaceSectionOpen, setWorkspaceSectionOpen] = useState(
    !!localStorage.getItem("workspaceSectionOpen")
      ? localStorage.getItem("workspaceSectionOpen") === "true"
      : true
  );
  useEffect(() => {
    localStorage.setItem("workspaceSectionOpen", String(workspaceSectionOpen));
  }, [workspaceSectionOpen]);

  const [appSectionOpen, setAppSectionOpen] = useState(
    !!localStorage.getItem("appSectionOpen")
      ? localStorage.getItem("appSectionOpen") === "true"
      : true
  );
  useEffect(() => {
    localStorage.setItem("appSectionOpen", String(appSectionOpen));
  }, [appSectionOpen]);

  const { instance } = useGetInstance();
  const [workspace, setWorkspace] = useState<Workspace>({
    type: "instance",
    id: instance?._id.toString() ?? "",
  });
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    if (instance) {
      setWorkspace({
        type: "instance",
        id: instance._id.toString(),
      });
    }
  }, [instance]);

  useEffect(() => {
    if (!slug) {
      setWorkspace({ type: "instance", id: instance?._id.toString() ?? "" });
    } else {
      const space = sharedSpaces
        ?.filter((s) => s.type === "external")
        .find((s) => s.instance.slug === slug);
      if (space) {
        setWorkspace({ type: "shared-space", id: space._id.toString() });
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (slug && sharedSpaces) {
      const space = sharedSpaces
        .filter((s) => s.type === "external")
        .find((s) => s.instance.slug === slug);
      if (space) {
        setWorkspace({ type: "shared-space", id: space._id.toString() });
      }
    }
  }, [slug, sharedSpaces?.length]);

  const isAnAppActive = useMemo(() => {
    return (
      settings?.apps?.timeTracking.isEnabled ||
      settings?.apps?.resourceManagement.isEnabled ||
      // settings?.apps?.billing ||
      false
    );
  }, [settings]);

  return (
    <div className="flex h-screen w-56 shrink-0 flex-col border-r border-gray-200/70">
      {/* bg-neutral-100/80 */}
      {((!!slug && !!sharedSpaces) || !slug) && (
        <>
          {!!instance ? (
            <WorkspaceSelector
              instance={{
                name: instance.name,
                _id: instance._id.toString(),
              }}
              workspace={workspace}
              setWorkspace={setWorkspace}
            />
          ) : (
            <div className="flex h-12 items-center border-b border-b-gray-200/70 px-3">
              <div className="h-7 w-full animate-pulse rounded-md bg-gray-200" />
            </div>
          )}
          {workspace.type === "instance" ? (
            <div className="relative flex-1 overflow-auto">
              <div className="pointer-events-none absolute inset-x-0 bottom-0  z-20 h-14 bg-gradient-to-b from-transparent via-white/80 to-white" />
              <div className="h-full overflow-auto px-3 pt-3.5 pb-14">
                <Notifications workspace={workspace} />
                {/* <Tab
                  Icon={
                    <InboxIcon
                      className={classNames(
                        location.pathname.startsWith("/inbox")
                          ? "text-gray-800"
                          : "text-gray-700 group-hover:text-gray-800",
                        "mr-2.5 h-4 w-4"
                      )}
                      aria-hidden="true"
                    />
                  }
                  tab={{
                    name: "Inbox",
                    route: "/inbox",
                  }}
                /> */}
                <Tab
                  Icon={
                    <RadiobuttonIcon
                      className={classNames(
                        location.pathname.startsWith("/my-tasks")
                          ? "text-gray-800"
                          : "text-gray-700 group-hover:text-gray-800",
                        "mr-2.5 h-4 w-4"
                      )}
                      aria-hidden="true"
                    />
                  }
                  tab={{
                    name: "My Tasks",
                    route: "/my-tasks",
                  }}
                />
                <MyProjectUpdates />
                {/* <Tab
                Icon={
                  <ChatBubbleLeftEllipsisIcon
                    className={classNames(
                      location.pathname.startsWith("/chats")
                        ? "text-gray-800"
                        : "text-gray-700 group-hover:text-gray-800",
                      "mr-2.5 h-4 w-4"
                    )}
                    aria-hidden="true"
                  />
                }
                tab={{
                  name: "Chats",
                  route: "/chats",
                }}
              /> */}
                <div className="pt-5">
                  <div
                    className="group flex h-7 cursor-pointer items-center space-x-1.5 rounded-md px-2 font-light text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    onClick={() => {
                      setWorkspaceSectionOpen((prev) => !prev);
                    }}
                  >
                    <div className="select-none text-xs">Workspace</div>
                    <TriangleRightIcon
                      className={clsx(
                        "h-3.5 w-3.5 text-gray-300 transition-all group-hover:text-gray-400/80",
                        workspaceSectionOpen && "rotate-90 transition-transform"
                      )}
                    />
                  </div>
                  {workspaceSectionOpen && (
                    <div className="pt-0.5">
                      {/* <Tab
                  Icon={
                    <TargetIcon
                      className={classNames(
                        location.pathname.startsWith("/goals")
                          ? "text-gray-800"
                          : "text-gray-700 group-hover:text-gray-800",
                        "mr-2.5 h-4 w-4"
                      )}
                      aria-hidden="true"
                    />
                  }
                  tab={{
                    name: "Goals",
                    route: "/goals",
                  }}
                /> */}
                      {/* <Tab
                  Icon={
                    <LayersIcon
                      className={classNames(
                        location.pathname.startsWith("/views")
                          ? "text-gray-800"
                          : "text-gray-700 group-hover:text-gray-800",
                        "mr-2.5 h-4 w-4"
                      )}
                      aria-hidden="true"
                    />
                  }
                  tab={{
                    name: "Views",
                    route: "/views",
                  }}
                /> */}

                      <Tab
                        Icon={
                          <BuildingOffice2Icon
                            className={classNames(
                              location.pathname.startsWith("/companies")
                                ? "text-gray-800"
                                : "text-gray-700 group-hover:text-gray-800",
                              "mr-2.5 h-4 w-4"
                            )}
                            aria-hidden="true"
                          />
                        }
                        tab={{
                          name: "Companies",
                          route: "/companies",
                        }}
                      />
                      <Tab
                        Icon={
                          <div className="mr-2.5 flex w-4 justify-center">
                            <BoltIcon />
                          </div>
                        }
                        tab={{
                          name: "Tasks",
                          route: "/all-tasks",
                          strictEquals: true,
                        }}
                      />
                      <Tab
                        Icon={
                          <ChartBarIcon
                            className={classNames(
                              location.pathname.startsWith("/analytics")
                                ? "text-gray-800"
                                : "text-gray-700 group-hover:text-gray-800",
                              "mr-2.5 h-4 w-4"
                            )}
                            aria-hidden="true"
                          />
                        }
                        tab={{
                          name: "Analytics",
                          route: "/analytics",
                        }}
                      />
                      {/* <Tab
                        Icon={
                          <div className="mr-2.5 flex h-4 w-4 items-center justify-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="1.5"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              className="h-4 w-4 rotate-180"
                            >
                              <circle cx="12" cy="18" r="3" />
                              <circle cx="6" cy="6" r="3" />
                              <circle cx="18" cy="6" r="3" />
                              <path d="M18 9v2c0 .6-.4 1-1 1H7c-.6 0-1-.4-1-1V9" />
                              <path d="M12 12v3" />
                            </svg>
                          </div>
                        }
                        tab={{
                          name: "Workflows",
                          route: "/workflows",
                          strictEquals: true,
                        }}
                      /> */}
                      <Tab
                        Icon={
                          <div className="mr-2.5 flex w-4 justify-center">
                            <SpaceSymbol color="gray" />
                          </div>
                        }
                        tab={{
                          name: "Spaces",
                          route: "/spaces",
                          strictEquals: true,
                        }}
                      />
                    </div>
                  )}
                </div>

                {isAnAppActive && (
                  <div
                    className={clsx("", workspaceSectionOpen ? "pt-5" : "pt-3")}
                  >
                    <div
                      className="group flex h-7 cursor-pointer items-center space-x-1.5 rounded-md px-2 font-light text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                      onClick={() => {
                        setAppSectionOpen((prev) => !prev);
                      }}
                    >
                      <div className="select-none text-xs">Apps</div>
                      <TriangleRightIcon
                        className={clsx(
                          "h-3.5 w-3.5 text-gray-300 transition-all group-hover:text-gray-400/80",
                          appSectionOpen && "rotate-90 transition-transform"
                        )}
                      />
                    </div>
                    {appSectionOpen && (
                      <div className="pt-0.5">
                        {settings?.apps?.timeTracking.isEnabled && (
                          <Tab
                            Icon={
                              <StopwatchIcon
                                className={classNames(
                                  location.pathname.startsWith("/time-tracking")
                                    ? "text-gray-800"
                                    : "text-gray-700 group-hover:text-gray-800",
                                  "mr-2.5 h-4 w-4"
                                )}
                                aria-hidden="true"
                              />
                            }
                            tab={{
                              name: "Time Tracking",
                              route: "/time-tracking",
                            }}
                          />
                        )}
                        {settings?.apps?.resourceManagement.isEnabled && (
                          <Tab
                            Icon={
                              <UserGroupIcon
                                className={classNames(
                                  location.pathname.startsWith("/resources")
                                    ? "text-gray-800"
                                    : "text-gray-700 group-hover:text-gray-800",
                                  "mr-2.5 h-4 w-4"
                                )}
                                aria-hidden="true"
                              />
                            }
                            tab={{
                              name: "Resources",
                              route: "/resources",
                              strictEquals: true,
                            }}
                          />
                        )}
                        {/* <Tab
                          Icon={
                            <AcademicCapIcon
                              className={classNames(
                                location.pathname.startsWith("/lms")
                                  ? "text-gray-800"
                                  : "text-gray-700 group-hover:text-gray-800",
                                "mr-2.5 h-4 w-4"
                              )}
                              aria-hidden="true"
                            />
                          }
                          tab={{
                            name: "LMS",
                            route: "/lms",
                            strictEquals: true,
                          }}
                        /> */}
                      </div>
                    )}
                  </div>
                )}

                <div className={clsx("", appSectionOpen ? "pt-5" : "pt-3")}>
                  <div
                    className="group flex h-7 cursor-pointer items-center space-x-1.5 rounded-md px-2 font-light text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    onClick={() => {
                      setSpacesOpen((prev) => !prev);
                    }}
                  >
                    <div className="select-none text-xs">My Spaces</div>
                    <TriangleRightIcon
                      className={clsx(
                        "h-3.5 w-3.5 text-gray-300 transition-all group-hover:text-gray-400/80",
                        spacesOpen && "rotate-90 transition-transform"
                      )}
                    />
                  </div>
                  {spacesOpen && (
                    <div className="pt-0.5">
                      {spaces?.map((space) => (
                        <Space space={space} key={space.slug} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="relative flex-1 overflow-auto">
              {/* <div className="pointer-events-none absolute inset-x-0 bottom-0  z-20 h-14 bg-gradient-to-b from-transparent via-white/80 to-white" /> */}
              <div className="h-full overflow-auto px-3 pt-3.5 pb-14">
                <Notifications workspace={workspace} />
                <Tab
                  Icon={
                    <RocketLaunchIcon
                      className={classNames(
                        location.pathname.includes("projects")
                          ? "text-gray-800"
                          : "text-gray-700 group-hover:text-gray-800",
                        "mr-2.5 h-4 w-4"
                      )}
                      aria-hidden="true"
                    />
                  }
                  tab={{
                    name: "Projects",
                    route: `/${slug}/projects`,
                    strictEquals: false,
                  }}
                />
              </div>
            </div>
          )}
          <div className="flex h-12 items-center justify-between border-t border-gray-200/70 px-3">
            {!slug ? (
              <Link to={"/settings"}>
                <Button
                  variant={"ghost"}
                  size={"icon"}
                  className="h-7 w-7 text-gray-500"
                >
                  <Cog6ToothIcon className="h-4 w-4" />
                </Button>
              </Link>
            ) : (
              <div></div>
            )}

            <DropdownMenu
              open={profileOpen}
              onOpenChange={(isOpen) => {
                setProfileOpen(isOpen);
              }}
            >
              <DropdownMenuTrigger className="outline-none ring-0 focus:outline-none">
                {/* <Button
              variant={"ghost"}
              size={"icon"}
              className="h-7 w-7 text-gray-400"
              onClick={() => {
                setProfileOpen((prev) => !prev);
              }}
            >
              <Cog6ToothIcon className="h-4 w-4" />
            </Button> */}
                <div className="flex h-7 w-7 select-none items-center justify-center rounded-full border text-[11px] font-light text-gray-500 shadow-sm outline-none hover:bg-gray-50">
                  {getFirstAndLastInitials(app.currentUser?.customData?.name)}
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    className="py-1"
                    onClick={() => {
                      app.logOut();
                    }}
                  >
                    <ArrowLeftOnRectangleIcon className="mr-2 h-3.5 w-3.5" />
                    <span className="text-sm">Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </>
      )}
    </div>
  );
}

function MyProjectUpdates() {
  const { data: internalUpdates } = useGetPendingInternalUpdatesForUser();
  const location = useLocation();
  const count = useMemo(() => {
    return (
      internalUpdates?.filter((update) => {
        const dueDate = dayjs(update.dueDate);
        if (dueDate.isBefore(dayjs().add(1, "day"))) {
          return true;
        }
      }) ?? []
    ).length;
  }, [internalUpdates]);
  if (!internalUpdates?.length) return null;
  return (
    <Tab
      Icon={
        <div className="mr-2.5 inline-flex h-4 w-4 items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={classNames(
              location.pathname.startsWith("/my-project-updates")
                ? "text-gray-800"
                : "text-gray-700 group-hover:text-gray-800",
              "h-3.5 w-3.5"
            )}
          >
            <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" />
          </svg>
        </div>
      }
      tab={{
        name: "My Updates",
        route: "/my-project-updates",
        count: count,
      }}
    />
  );
}

function Space({ space }: { space: SpaceData }) {
  const [spaceOpen, setSpaceOpen] = useState(
    localStorage.getItem(`space_${space.slug}_open`) === "true"
  );
  const [optionsOpen, setOptionsOpen] = useState(false);
  useEffect(() => {
    localStorage.setItem(`space_${space.slug}_open`, String(spaceOpen));
  }, [spaceOpen]);

  const { phaseId } = useParams();

  const location = useLocation();

  const [projectIsInSpace, setProjectIsInSpace] = useState(false);

  useEffect(() => {
    if (!phaseId) {
      setProjectIsInSpace(false);
    }
  }, [phaseId]);
  const navigate = useNavigate();

  return (
    <div>
      {phaseId && (
        <ProjectIsInSpace
          projectId={phaseId}
          spaceId={space._id}
          setProjectIsInSpace={setProjectIsInSpace}
        />
      )}
      <div className="relative">
        <div className="group cursor-pointer select-none pb-0.5">
          <div
            className={clsx(
              "group flex h-7 items-center justify-between space-x-2.5 rounded-md px-2 pr-1 text-gray-600 group-hover:bg-gray-100 group-hover:text-gray-700",
              optionsOpen && "bg-gray-100 text-gray-700"
            )}
            onClick={() => {
              setSpaceOpen((prev) => !prev);
            }}
          >
            <div className="flex w-4 justify-center">
              <SpaceSymbol color={space.color} />
            </div>
            <div className="flex min-w-0 flex-1 items-center space-x-1.5">
              <div className="truncate whitespace-nowrap text-sm text-gray-700">
                {space.name}
              </div>
              <TriangleRightIcon
                className={clsx(
                  "h-3.5 w-3.5 shrink-0 text-gray-300 transition-all group-hover:text-gray-400/80",
                  spaceOpen && "rotate-90 transition-transform"
                )}
              />
            </div>
            <div className="flex items-center justify-between space-x-1">
              <DropdownMenu
                open={optionsOpen}
                onOpenChange={(isOpen) => {
                  setOptionsOpen(isOpen);
                }}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={"ghost"}
                    size={"icon"}
                    className={clsx(
                      "h-5 w-5 rounded opacity-0 hover:bg-gray-200 group-hover:opacity-100",
                      optionsOpen && "bg-gray-200 opacity-100"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <EllipsisHorizontalIcon className="h-3.5 w-3.5" />
                  </Button>
                  {/* <Button
                    variant={"ghost"}
                    className={clsx(
                      "h-7 w-7 rounded p-0 hover:bg-gray-200/60 group-hover:visible",
                      !open ? "invisible" : "bg-gray-200/60"
                    )}
                  >
                    <EllipsisVerticalIcon className="h-3.5 w-3.5" />
                  </Button> */}
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-44" align="start">
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      // className="py-1 focus:bg-gray-50 focus:text-red-600"
                      onClick={(e) => {
                        navigate(`/settings/spaces/${space._id}`);
                      }}
                      className="py-1"
                    >
                      <Cog6ToothIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="ml-2 text-sm">Settings</span>
                    </DropdownMenuItem>

                    {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        {spaceOpen && (
          <div className="flex h-full space-x-2 pl-[26px] pb-2">
            {/* <div className="w-[1.5px] shrink-0 rounded-md bg-gray-300" /> */}
            <div className="flex-1 space-y-0.5">
              <Link
                className={clsx(
                  "flex h-7 cursor-pointer items-center space-x-2.5 rounded-md px-2 hover:bg-gray-100 hover:text-gray-700",
                  (location.pathname.startsWith(
                    `/spaces/${space._id}/projects`
                  ) ||
                    projectIsInSpace) &&
                    "bg-gray-100 text-gray-700"
                )}
                to={`/spaces/${space._id}/projects`}
              >
                {/* <div className="h-1.5 w-1.5 rounded-full bg-gray-400"
                  style={{ backgroundColor: space.color}}
                /> */}
                <RocketLaunchIcon className="h-4 w-4" />
                <div className="text-sm text-gray-700">Projects</div>
              </Link>
              <Link
                to={`/spaces/${space._id}/tasks`}
                className={clsx(
                  "flex h-7 cursor-pointer items-center space-x-2.5 rounded-md px-2  hover:bg-gray-100",
                  location.pathname.startsWith(`/spaces/${space._id}/tasks`)
                    ? "bg-gray-100 text-gray-800"
                    : "hover:text-gray-700"
                  // isProjects ? "text-gray-400" : "text-gray-500"
                )}
              >
                <BoltIcon className="h-4 w-4" />
                {/* <div className="h-1.5 w-1.5 rounded-full bg-gray-400"
                  style={{ backgroundColor: space.color}}
                /> */}
                <div className="text-sm text-gray-700">Tasks</div>
              </Link>
              {/* <div
                className={clsx(
                  "flex h-7 cursor-pointer items-center space-x-2.5 rounded-md px-2 hover:bg-gray-100 hover:text-gray-700"
                  // isProjects ? "text-gray-400" : "text-gray-500"
                )}
                onClick={() => {
                  // setIsProjects(!isProjects);
                }}
              >
                <ChartBarIcon className="h-4 w-4" />
                <div className="text-sm text-gray-700">Analytics</div>
              </div> */}
              <Link
                className={clsx(
                  "flex h-7 cursor-pointer items-center space-x-2.5 rounded-md px-2 hover:bg-gray-100",
                  location.pathname.startsWith(`/spaces/${space._id}/templates`)
                    ? "bg-gray-100 text-gray-800"
                    : "hover:text-gray-700"
                  // isProjects ? "text-gray-400" : "text-gray-500"
                )}
                to={`/spaces/${space._id}/templates`}
              >
                <RectangleStackIcon className="h-4 w-4" />
                {/* <div className="h-1.5 w-1.5 rounded-full bg-gray-400"
                  style={{ backgroundColor: space.color}}
                /> */}
                <div className="text-sm text-gray-700">Templates</div>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectIsInSpace({
  projectId,
  spaceId,
  setProjectIsInSpace,
}: {
  projectId: string;
  spaceId: string;
  setProjectIsInSpace: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { data: phase } = useGetPhase(projectId);
  useEffect(() => {
    if (phase && phase.spaceId === spaceId) {
      setProjectIsInSpace(true);
    } else {
      setProjectIsInSpace(false);
    }
  }, [phase]);
  return <></>;
}

function Tab({
  Icon,
  tab,
}: {
  Icon: React.ReactNode;
  tab: {
    name: string;
    route: string;
    strictEquals?: boolean;
    count?: number;
  };
}) {
  const location = useLocation();
  return (
    <Link to={tab.route}>
      <div className="group select-none pb-0.5">
        <div
          className={classNames(
            (
              tab.strictEquals
                ? location.pathname === tab.route
                : location.pathname.startsWith(tab.route)
            )
              ? "bg-gray-100 text-gray-800"
              : "text-gray-700 group-hover:bg-gray-100 group-hover:text-gray-800",
            "group flex h-[28px] items-center rounded-md px-2 text-sm"
          )}
        >
          {Icon}
          <div className="flex-1">{tab.name}</div>
          {!!tab.count && <Count variant={"primary"}>{tab.count}</Count>}
        </div>
      </div>
    </Link>
  );
}

function Subtab({
  Icon,
  tab,
}: {
  Icon: React.ReactNode;
  tab: {
    name: string;
    route: string;
  };
}) {
  const location = useLocation();
  return (
    <Link to={tab.route}>
      <div className="group select-none pb-0.5">
        <div
          className={classNames(
            location.pathname.startsWith(tab.route)
              ? "bg-gray-100 text-gray-800"
              : "text-gray-700 group-hover:bg-gray-100 group-hover:text-gray-800",
            "group flex h-[28px] items-center rounded-md px-2 text-sm"
          )}
        >
          {Icon}
          <div className="">{tab.name}</div>
        </div>
      </div>
    </Link>
  );
}

export function SpaceSymbol({ color }: { color: string }) {
  return (
    <div
      className={`flex h-[13px] w-[13px] items-center justify-center rounded border-[1.5px]`}
      style={{ borderColor: color, opacity: 0.9 }}
    >
      <div
        className={`flex h-[7px] w-[7px] items-center justify-center rounded-sm`}
        style={{ backgroundColor: color, opacity: 0.7 }}
      />
    </div>
  );
}

type Workspace = { type: "instance" | "shared-space"; id: string };

export function WorkspaceSelector({
  instance,
  workspace,
  setWorkspace,
}: {
  instance: { name: string; _id: string };
  workspace: Workspace;
  setWorkspace: (workspace: Workspace) => void;
}) {
  const { sharedSpaces } = useGetSharedSpacesForUser();
  const [open, setOpen] = useState(false);
  const { data: unreadNotifications } = useGetUnreadNotifications();
  let unreadNotificationsCount = 0;
  if (workspace.type === "instance") {
    unreadNotificationsCount =
      sharedSpaces
        ?.filter((s) => s.type === "external")
        .reduce((acc, s) => acc + s.notificationsCount, 0) ?? 0;
  } else {
    unreadNotificationsCount =
      sharedSpaces
        ?.filter(
          (s) => s._id.toString() !== workspace.id && s.type === "external"
        )
        .reduce((acc, s) => acc + s.notificationsCount, 0) ?? 0;
    unreadNotificationsCount += unreadNotifications?.length ?? 0;
  }

  const navigate = useNavigate();

  return (
    <div className="">
      <DropdownMenu
        open={open}
        onOpenChange={(open) => {
          setOpen(open);
        }}
      >
        <DropdownMenuTrigger asChild>
          <div
            onClick={() => {
              setOpen(true);
            }}
            className="flex h-12 w-full cursor-pointer items-center justify-between border-b border-b-gray-200/70 px-5 text-sm font-normal text-gray-700 hover:bg-gray-100 hover:text-gray-800"
          >
            <div className="flex items-center gap-2">
              {/* <img className="h-5 w-5 rounded-sm" src={logo?.url} /> */}
              <div>
                {workspace.type === "instance"
                  ? instance.name
                  : sharedSpaces?.find((s) => s._id.toString() === workspace.id)
                      ?.instance.name}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <CaretSortIcon className="h-4 w-4 text-transparent" />
              {!!unreadNotificationsCount && (
                <div className="flex h-[17px] items-center justify-center rounded bg-primary-main px-[4px] text-[10px] text-white">
                  {unreadNotificationsCount}
                </div>
              )}
            </div>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="-mt-2.5 ml-2 w-60 p-1.5">
          <DropdownMenuGroup>
            <div className="px-1 pb-1 text-xs text-gray-400">Workspaces</div>
            <DropdownMenuItem
              className={clsx(
                "group flex h-8 items-center justify-between gap-3 truncate px-3 transition-none",
                instance._id.toString() === workspace.id &&
                  "bg-gray-100 text-gray-600 focus:bg-gray-100 focus:text-gray-600"
              )}
              onClick={() => {
                setWorkspace({ type: "instance", id: instance._id.toString() });
                navigate("/my-tasks");
                setOpen(false);
              }}
            >
              <div className="flex-1 truncate">{instance.name}</div>
              {!!unreadNotifications?.length &&
                workspace.type !== "instance" && (
                  <div className="flex h-[17px] min-w-[15px] items-center justify-center rounded bg-primary-main px-1 text-[10px] text-white transition-all">
                    {unreadNotifications.length}
                  </div>
                )}
            </DropdownMenuItem>
            {!!sharedSpaces?.filter((s) => s.type === "external").length && (
              <>
                {/* <DropdownMenuSeparator /> */}
                <div className="space-y-0.5">
                  <div className="px-1 pb-0.5 pt-2 text-xs text-gray-400">
                    Shared Spaces
                  </div>
                  {sharedSpaces
                    ?.filter((s) => s.type === "external")
                    .map((s) => (
                      <DropdownMenuItem
                        key={s._id.toString()}
                        onClick={() => {
                          setWorkspace({
                            type: "shared-space",
                            id: s._id.toString(),
                          });
                          navigate(`/${s.instance.slug}/projects`);
                          setOpen(false);
                        }}
                        className={clsx(
                          "group flex justify-between gap-3 px-3 transition-none",
                          workspace.id === s._id.toString() &&
                            "bg-gray-100 text-gray-600 focus:bg-gray-100 focus:text-gray-600"
                        )}
                      >
                        <div className="flex-1 truncate">
                          {s?.instance?.name}
                        </div>
                        {!!s.notificationsCount &&
                          workspace.id !== s._id.toString() && (
                            <div className="[15px] flex h-[17px] items-center justify-center rounded bg-primary-main px-1 text-center text-[10px] text-white transition-all">
                              {s.notificationsCount}
                            </div>
                          )}
                      </DropdownMenuItem>
                    ))}
                </div>
              </>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
  {
    /* <Select>
        <SelectTrigger className={`w-fit gap-2 bg-white focus:ring-0`}>
          <div className="inline-flex items-center gap-2">
            {value.type === "instance"
              ? instance.name
              : sharedSpaces?.find((s) => s._id.toString() === value.id)
                  ?.instance.name}
          </div>
        </SelectTrigger>
        <SelectContent className="[12rem]">
          <SelectGroup className="p-0">
            <div className="px-1 py-0.5 text-xs text-gray-400">Workspaces</div>
            <SelectItem
              value="instance"
              onClick={() => {
                setValue({ type: "instance", id: instance._id.toString() });
                setOpen(false);
              }}
              className="flex items-center"
            >
              <div className="flex items-center">{instance.name}</div>
            </SelectItem>
            {!!sharedSpaces?.length && (
              <>
                <SelectSeparator />
                <div>
                  <div className="px-1 py-0.5 text-xs text-gray-400">
                    Shared Spaces
                  </div>
                  {sharedSpaces
                    ?.filter((s) => s.type === "external")
                    .map((s) => (
                      <SelectItem
                        key={s._id.toString()}
                        value="shared-space"
                        onClick={() => {
                          setValue({
                            type: "shared-space",
                            id: s._id.toString(),
                          });
                          setOpen(false);
                        }}
                        className="pr-12"
                      >
                        <div className="flex w-full items-center justify-between">
                          <div className="flex-1 truncate">
                            {s?.instance?.name}
                          </div>
                          {!!s.notificationsCount && (
                            <div className="absolute right-1.5 [18px] rounded bg-primary-main px-1 py-0.5 text-center text-xs text-white">
                              {s.notificationsCount}
                            </div>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                </div>
              </>
            )}
          </SelectGroup>
        </SelectContent>
      </Select> */
  }
}

function InstanceNotifications() {
  const { data: unreadNotifications } = useGetUnreadNotifications();
  const { data: allNotifications } = useGetAllNotifications();
  function handleClose() {
    setOpen(false);
  }
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"unread" | "all">("unread");

  useEffect(() => {
    if (
      !!unreadNotifications &&
      unreadNotifications.length === 0 &&
      tab === "unread"
    ) {
      setTab("all");
    } else if (!!unreadNotifications && unreadNotifications.length > 0) {
      setTab("unread");
    }
  }, [unreadNotifications]);

  return (
    <Popover.Root open={open} onOpenChange={(open) => setOpen(open)}>
      <Popover.Trigger asChild>
        <div className="group select-none pb-0.5">
          <div
            className={clsx(
              "group flex h-[28px] w-full cursor-pointer items-center rounded-md px-2 text-gray-700 hover:bg-gray-100 group-hover:text-gray-800",
              open && "bg-gray-100"
            )}
            onClick={() => {
              setOpen(!open);
            }}
          >
            <AnimatePresence initial={false}>
              <motion.div
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {!!unreadNotifications?.length ? (
                  <BellAlertIcon
                    className={clsx(
                      "mr-2.5 h-4 w-4 text-gray-700 group-hover:text-gray-800"
                    )}
                  />
                ) : (
                  <BellIcon
                    className={clsx(
                      "mr-2.5 h-4 w-4 text-gray-700 group-hover:text-gray-800"
                    )}
                  />
                )}
              </motion.div>

              <motion.div
                key="count"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                layout
                className="flex flex-1 items-center justify-between "
              >
                <span className="text-sm text-gray-700 group-hover:text-gray-800">
                  Notifications
                </span>
                {!!unreadNotifications && unreadNotifications.length > 0 && (
                  <span className="flex h-[17px] min-w-[15px] items-center justify-center rounded bg-primary-main px-1 py-0.5 text-[10px] text-white">
                    {unreadNotifications.length}
                  </span>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <>
          <Popover.Content
            side="right"
            align="start"
            sideOffset={4}
            className="relative z-50 flex max-h-[70vh] min-h-[30vh] w-[700px] flex-col rounded-md bg-white p-0 text-gray-800 shadow-md outline-none ring-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
          >
            <Tabs
              defaultValue={
                !!unreadNotifications && unreadNotifications.length > 0
                  ? "unread"
                  : "all"
              }
              value={tab}
              onValueChange={(value) => {
                setTab(value as "unread" | "all");
              }}
              className="flex flex-1 flex-col overflow-auto pt-3"
            >
              <TabsList className="z-10 shrink-0 space-x-5 px-4">
                <TabsTrigger
                  className="pb-1.5"
                  value="unread"
                  disabled={
                    !unreadNotifications ||
                    (!!unreadNotifications && unreadNotifications.length === 0)
                  }
                >
                  Unread
                </TabsTrigger>
                <TabsTrigger className="pb-1.5" value="all">
                  All
                </TabsTrigger>
              </TabsList>
              <div className="flex-1 overflow-auto">
                <TabsContent
                  value="unread"
                  className="h-full overflow-auto py-1 focus-visible:ring-0"
                >
                  <div className="divide-y divide-gray-100">
                    {unreadNotifications?.map((n) => (
                      <Notification
                        key={n._id}
                        notification={n}
                        handleClose={handleClose}
                      />
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="all" className="h-full overflow-auto py-1">
                  <div className="divide-y divide-gray-100">
                    {allNotifications?.notifications?.map((n) => (
                      <Notification
                        key={n._id}
                        notification={n}
                        handleClose={handleClose}
                      />
                    ))}
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </Popover.Content>
          <div className="fixed top-0 left-0 z-20 h-full w-full bg-black bg-opacity-25" />
        </>
      </Popover.Portal>
    </Popover.Root>
  );
}

function SharedSpaceNotifications({ workspace }: { workspace: Workspace }) {
  const { data: unreadNotifications } =
    useGetUnreadNotificationsForSharedSpaceForUser(workspace.id);
  const { data: allNotifications } = useGetNotificationsForSharedSpaceForUser(
    workspace.id
  );

  function handleClose() {
    setOpen(false);
  }
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"unread" | "all">("unread");

  useEffect(() => {
    if (
      !!unreadNotifications &&
      unreadNotifications.length === 0 &&
      tab === "unread"
    ) {
      setTab("all");
    } else if (!!unreadNotifications && unreadNotifications.length > 0) {
      setTab("unread");
    }
  }, [unreadNotifications]);

  return (
    <Popover.Root open={open} onOpenChange={(open) => setOpen(open)}>
      <Popover.Trigger asChild>
        <div className="group select-none pb-0.5">
          <div
            className={clsx(
              !!unreadNotifications && unreadNotifications.length > 0
                ? "text-gray-700"
                : "group-hover:text-gray-800",
              "group flex h-[28px] w-full cursor-pointer items-center rounded-md pl-2 pr-2 hover:bg-gray-100",
              open && "bg-gray-100"
            )}
            onClick={() => {
              setOpen(!open);
            }}
          >
            <AnimatePresence initial={false}>
              <motion.div
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <BellIcon
                  className={clsx(
                    "mr-2.5 h-4 w-4 shrink-0 text-gray-700 group-hover:text-gray-800"
                  )}
                />
              </motion.div>

              <motion.div
                key="count"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                layout
                className="flex flex-1 items-center justify-between "
              >
                <span className="text-sm text-gray-700 group-hover:text-gray-800">
                  Notifications
                </span>
                {!!unreadNotifications && unreadNotifications.length > 0 && (
                  <span className="flex h-[17px] min-w-[15px] items-center justify-center rounded bg-primary-main px-1 py-0.5 text-[10px] text-white">
                    {unreadNotifications.length}
                  </span>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <>
          <Popover.Content
            side="right"
            align="start"
            sideOffset={4}
            className="relative z-50 flex max-h-[70vh] min-h-[30vh] w-[700px] flex-col rounded-md bg-white p-0 text-gray-800 shadow-md outline-none ring-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"
          >
            <Tabs
              defaultValue={
                !!unreadNotifications && unreadNotifications.length > 0
                  ? "unread"
                  : "all"
              }
              value={tab}
              onValueChange={(value) => {
                setTab(value as "unread" | "all");
              }}
              className="flex flex-1 flex-col overflow-auto pt-3"
            >
              <TabsList className="z-10 shrink-0 space-x-5 px-4">
                <TabsTrigger
                  className="pb-1.5"
                  value="unread"
                  disabled={
                    !unreadNotifications ||
                    (!!unreadNotifications && unreadNotifications.length === 0)
                  }
                >
                  Unread
                </TabsTrigger>
                <TabsTrigger className="pb-1.5" value="all">
                  All
                </TabsTrigger>
              </TabsList>
              <div className="flex-1 overflow-auto">
                <TabsContent
                  value="unread"
                  className="h-full overflow-auto py-1 focus-visible:ring-0"
                >
                  <div className="divide-y divide-gray-100">
                    {unreadNotifications?.map((n) => (
                      <SharedSpaceNotification
                        key={n._id}
                        notification={n}
                        handleClose={handleClose}
                        sharedSpaceId={workspace.id}
                      />
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="all" className="h-full overflow-auto py-1">
                  <div className="divide-y divide-gray-100">
                    {allNotifications?.notifications?.map((n) => (
                      <SharedSpaceNotification
                        key={n._id}
                        notification={n}
                        handleClose={handleClose}
                        sharedSpaceId={workspace.id}
                      />
                    ))}
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </Popover.Content>
          <div className="fixed top-0 left-0 z-10 h-full w-full bg-black bg-opacity-25" />
        </>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function Notifications({ workspace }: { workspace: Workspace }) {
  if (workspace.type === "instance") {
    return <InstanceNotifications />;
  } else {
    return <SharedSpaceNotifications workspace={workspace} />;
  }

  // const {data: allNotifications} = useGetAllNotifications()

  // const { data: notifications } = useGetNotificationsForUser();
  // // console.log(notifications);
  // const numberOfUnreadNotifications =
  //   notifications?.unreadNotifications.length ?? 0;
  // const allNotifications = [
  //   ...(notifications?.unreadNotifications ?? []),
  //   ...(notifications?.readNotifications ?? []),
  // ].sort((n1, n2) => {
  //   return new Date(n2.createdAt).getTime() - new Date(n1.createdAt).getTime();
  // });
  // const markAllNotificationsAsReadMutation = useMarkAllNotificationsAsRead();
}

function NotificationButton({
  color,
  text,
  notificationCount,
  onClick,
  isActive,
}: {
  color: string;
  text: string;
  notificationCount: number;
  onClick: () => void;
  isActive: boolean;
}) {
  return (
    <div
      className={clsx(
        `inline-flex h-6 items-center space-x-1.5 border border-${color}-400 px-2.5 text-sm text-${color}-700 rounded-full  shadow-sm hover:bg-${color}-50 cursor-pointer`,
        isActive && `bg-${color}-50 cursor-default pr-1`
      )}
      onClick={onClick}
    >
      <span className="">{text}</span>
      <span className="text-xs">{notificationCount}</span>
      {isActive && (
        <button
          className={clsx(
            "-pr-1 rounded-full p-0.5",
            `border-${color}-400 hover:bg-${color}-100`
          )}
        >
          <XMarkIcon
            className={`h-3 w-3 text-${color}-700 hover:text-${color}-900`}
          />
        </button>
      )}
    </div>
  );
}

function SharedSpaceNotification({
  notification,
  handleClose,
  sharedSpaceId,
}: {
  notification: NotificationData;
  handleClose: () => void;
  sharedSpaceId: string;
}) {
  const { update, emoji, type } = getNotificationUpdate(notification);
  const navigate = useNavigate();
  const location = useLocation();
  const markSharedSpaceNotificationAsReadMutation =
    useMarkSharedSpaceNotificationAsRead(sharedSpaceId);
  const { slug } = useParams();

  return (
    <>
      {update && (
        <div
          className={clsx(
            "group relative z-0 flex  items-start overflow-x-clip px-4 py-2.5",
            notification.type === "task" ||
              notification.type === "journey" ||
              notification.type === "thread" ||
              notification.type === "thread message"
              ? "cursor-pointer hover:bg-gray-50"
              : "cursor-default"
          )}
          onClick={() => {
            if (slug) {
              if (notification.type === "task") {
                const searchParams = new URLSearchParams(location.search);
                searchParams.set("taskId", notification.objectId);
                navigate({
                  pathname: location.pathname,
                  search: searchParams.toString(),
                });
                handleClose();
              } else if (notification.type === "thread") {
                navigate(
                  `${slug}/projects/${notification.metadata.phaseId}/chat?chatId=${notification.objectId}`
                );
                handleClose();
              } else if (notification.type === "thread message") {
                navigate(
                  `${slug}/projects/${notification.metadata.phaseId}/chat?chatId=${notification.metadata.threadId}`
                );
                handleClose();
              } else if (notification.type === "journey") {
                navigate(`${slug}/projects/${notification.metadata.phaseId}`);
                handleClose();
              }
              markSharedSpaceNotificationAsReadMutation.mutate({
                update: {
                  _id: new ObjectID(notification._id),
                },
              });
            }
          }}
        >
          <div className="inline-flex items-start">
            <div
              className={clsx(
                "mt-[7px] h-1.5 w-1.5 rounded-full",
                !notification.isRead && "bg-primary"
              )}
            />
            <div className="ml-4 flex items-center whitespace-nowrap pt-0.5 text-left text-xs text-gray-400">
              <time
                className="w-[50px]"
                dateTime={dayjs(notification.createdAt).format("MMM DD")}
              >
                {dayjs(notification.createdAt).format("MMM DD")}
              </time>
              <time
                className="w-[60px]"
                dateTime={dayjs(notification.createdAt).format("MMM DD")}
              >
                {dayjs(notification.createdAt).format("h:mm A")}
              </time>
            </div>

            <div className="w-20 px-4 text-sm">
              <NotificationEntity notification={notification} />
            </div>
            <div className="flex min-w-0 flex-1 justify-between space-x-4 text-sm">
              {update}
            </div>
          </div>
          {!notification.isRead && (
            <div className="absolute right-2 top-1.5 hidden transition-all group-hover:block">
              <Button
                variant={"secondary"}
                size="sm"
                onClick={(e) => {
                  markSharedSpaceNotificationAsReadMutation.mutate({
                    update: {
                      _id: new ObjectID(notification._id),
                    },
                  });
                  e.stopPropagation();
                }}
              >
                Mark as read
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function Notification({
  notification,
  handleClose,
  sharedSpaceId,
}: {
  notification: NotificationData;
  handleClose: () => void;
  sharedSpaceId?: string;
}) {
  const { update, emoji, type } = getNotificationUpdate(notification);
  const navigate = useNavigate();
  const location = useLocation();
  const markNotificationAsReadMutation = useMarkNotificationAsRead();

  return (
    <>
      {update && (
        <div
          className={clsx(
            "group relative z-0 flex  items-start overflow-x-clip px-4 py-2.5",
            notification.type === "task" ||
              notification.type === "journey" ||
              notification.type === "thread" ||
              notification.type === "thread message"
              ? "cursor-pointer hover:bg-gray-50"
              : "cursor-default"
          )}
          onClick={() => {
            if (notification.type === "task") {
              if (notification.subAction === "bulk create") {
                navigate(`/projects/${notification.metadata.phaseId}`);
                handleClose();
                return;
              }
              const searchParams = new URLSearchParams(location.search);
              searchParams.set("taskId", notification.objectId);
              navigate({
                pathname: location.pathname,
                search: searchParams.toString(),
              });
              handleClose();
            } else if (notification.type === "thread") {
              navigate(
                `/projects/${notification.metadata.phaseId}/chat?chatId=${notification.objectId}`
              );
              handleClose();
            } else if (notification.type === "thread message") {
              navigate(
                `/projects/${notification.metadata.phaseId}/chat?chatId=${notification.metadata.threadId}`
              );
              handleClose();
            } else if (notification.type === "journey") {
              navigate(`/projects/${notification.metadata.phaseId}`);
              handleClose();
            }
            markNotificationAsReadMutation.mutate({
              update: {
                _id: new ObjectID(notification._id),
              },
            });
          }}
        >
          <div className="inline-flex items-start">
            <div
              className={clsx(
                "mt-[7px] h-1.5 w-1.5 rounded-full",
                !notification.isRead && "bg-primary"
              )}
            />
            <div className="ml-4 flex items-center whitespace-nowrap pt-0.5 text-left text-xs text-gray-400">
              <time
                className="w-[50px]"
                dateTime={dayjs(notification.createdAt).format("MMM DD")}
              >
                {dayjs(notification.createdAt).format("MMM DD")}
              </time>
              <time
                className="w-[60px]"
                dateTime={dayjs(notification.createdAt).format("MMM DD")}
              >
                {dayjs(notification.createdAt).format("h:mm A")}
              </time>
            </div>

            <div className="w-28 px-4 text-sm">
              <NotificationEntity notification={notification} />
            </div>
            <div className="flex min-w-0 flex-1 justify-between space-x-4 text-sm">
              {update}
            </div>
          </div>
          {!notification.isRead && (
            <div className="absolute right-2 top-1.5 hidden transition-all group-hover:block">
              <Button
                variant={"secondary"}
                size="sm"
                onClick={(e) => {
                  markNotificationAsReadMutation.mutate({
                    update: {
                      _id: new ObjectID(notification._id),
                    },
                  });
                  e.stopPropagation();
                }}
              >
                Mark as read
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function NotificationEntity({
  notification,
}: {
  notification: NotificationData;
}) {
  switch (notification.type) {
    case "task":
      return <Entity type="Task" color="blue" />;
    case "journey":
      return <Entity type="Project" color="pink" />;
    case "thread":
      return <Entity type="Chat" color="green" />;
    case "thread message":
      return <Entity type="Chat" color="green" />;
    case "shared space invite":
      return <Entity type="Invite" color="yellow" />;
    case "integration":
      return <Entity type="Integration" color="purple" />;
    case "email log":
      return <Entity type="Email" color="purple" />;
  }
  return null;
}
function getNotificationUpdate(notification: NotificationData): {
  update: React.ReactNode;
  emoji?: string;
  type?: {
    color: "red" | "green" | "blue" | "yellow" | "gray" | "indigo" | "pink";
    text: string;
  };
} {
  if (notification.type === "task") {
    if (notification.action === "CREATE") {
      if (notification.subAction === "bulk create") {
        return {
          update: (
            <div>
              <span className="text-gray-700">{`${
                notification.metadata.count
              } task${notification.metadata.count === 1 ? "" : "s"} `}</span>
              <span className="text-gray-400">in the project</span>
              <span className="text-gray-700">{` ${notification.metadata.phaseName} `}</span>
              <span className="text-gray-400">
                {notification.metadata.count === 1
                  ? "has"
                  : "have" + " been created & assigned to you"}
              </span>
            </div>
          ),
          type: {
            color: "blue",
            text: "Task",
          },
        };
      }

      return {
        update: (
          <div>
            <span className="text-gray-700">{`${notification.metadata.taskTitle} `}</span>
            <span className="text-gray-400">
              has been created & assigned to you
            </span>
          </div>
        ),
        type: {
          color: "blue",
          text: "Task",
        },
      };
    } else if (notification.action === "UPDATE") {
      if (notification.subAction === "overdue") {
        return {
          update: (
            <div>
              <span className="text-gray-700">{`${notification.metadata.taskTitle} `}</span>
              <span className="text-gray-400">is now overdue</span>
              {/* <span className="font-medium text-gray-700">{` ${notification.metadata.taskerName} `}</span> */}
            </div>
          ),
          emoji: "🛑",
        };
      } else if (notification.subAction === "due date change") {
        return {
          update: (
            <div>
              <span className="text-gray-700">{`${notification.metadata.taskTitle} `}</span>
              <span className="text-gray-400">had its due date changed</span>
              {/* <span className="font-medium text-gray-700">{` ${notification.metadata.taskerName} `}</span> */}
            </div>
          ),
          // emoji: "",
        };
      } else if (notification.subAction === "blocker marked") {
        return {
          update: (
            <div>
              <span className="text-gray-700">{`${notification.metadata.taskTitle} `}</span>
              <span className="text-gray-400">was marked as </span>
              <span className="text-gray-700">stuck</span>
              {/* <span className="text-red-600">blocked</span> */}
              <span className="text-gray-400"> by</span>
              <span className="text-gray-700">{` ${notification.metadata.reporterName} `}</span>
              {/* <span className="font-medium text-gray-700">{` ${notification.metadata.taskerName} `}</span> */}
            </div>
          ),
          // emoji: "",
        };
      } else if (notification.subAction === "tasker change") {
        return {
          update: (
            <div>
              <span className="text-gray-700">{`${notification.metadata.taskTitle} `}</span>
              <span className="text-gray-400">tasker changed from </span>
              <span className="text-gray-700">
                {notification.metadata.previousTaskerName}
              </span>
              {/* <span className="text-red-600">blocked</span> */}
              <span className="text-gray-400"> to </span>
              <span className="text-gray-700">
                {notification.metadata.newTaskerName}
              </span>
              {/* <span className="font-medium text-gray-700">{` ${notification.metadata.taskerName} `}</span> */}
            </div>
          ),
          // emoji: "",
        };
      }
      // else if (notification.subAction === "blocker resolved") {
      //   return {
      //     update: (
      //       <div>
      //       <span className="text-gray-400">Task</span>
      //         <span className="text-gray-700">{` ${notification.metadata.taskTitle}`}</span>
      //         <span className="text-gray-400">'s blocker was resolved by </span>
      //         <span className="font-medium text-gray-700">{` ${notification.metadata.resolverName} `}</span>
      //         {/* <span className="font-medium text-gray-700">{` ${notification.metadata.taskerName} `}</span> */}
      //       </div>
      //     ),
      //     // emoji: "",
      //   };
      // }
    }
  } else if (notification.type === "journey") {
    if (notification.action === "CREATE") {
      return {
        update: (
          <div>
            <span className="text-gray-700">{`${notification.metadata.journeyName} `}</span>
            <span className="text-gray-400">was launched with</span>
            <span className="text-gray-700">{` ${notification.metadata.taskCount} tasks `}</span>
            <span className="text-gray-400">assigned to you</span>
          </div>
        ),
        emoji: "🚀",
      };
    } else if (notification.action === "UPDATE") {
      if (notification.subAction === "completion") {
        return {
          update: (
            <div>
              <span className="font-medium text-gray-700">{`${notification.metadata.journeyName} `}</span>
              <span className="text-gray-400">was completed!</span>
            </div>
          ),
          emoji: "🔥",
        };
      }
    }
  } else if (notification.type === "thread") {
    if (notification.action === "CREATE") {
      return {
        update: (
          <div>
            <span className="text-gray-700">{`${notification.metadata.threadName} `}</span>
            <span className="text-gray-400">was started by</span>
            <span className="text-gray-700">{` ${notification.metadata.createdByName} `}</span>
          </div>
        ),
        emoji: "🧵",
      };
    }
  } else if (notification.type === "thread message") {
    if (notification.action === "CREATE") {
      return {
        update: (
          <div>
            <span className="text-gray-700">{`${notification.metadata.threadName} `}</span>
            <span className="text-gray-400">has a new message from </span>
            <span className="text-gray-700">{` ${notification.metadata.createdByName} `}</span>
          </div>
        ),
      };
    }
  } else if (notification.type === "shared space timeline stage") {
    if (notification.action === "UPDATE") {
      if (notification.subAction === "start") {
        return {
          update: (
            <div>
              <Entity type="Shared space stage" color="gray" />
              <span className="font-medium text-gray-700">{` ${notification.metadata.name} `}</span>
              <span className="text-gray-400">started</span>
            </div>
          ),
        };
      } else if (notification.subAction === "completion") {
        return {
          update: (
            <div>
              <Entity type="Shared space stage" color="gray" />
              <span className="font-medium text-gray-700">{` ${notification.metadata.name} `}</span>
              <span className="text-gray-400">completed</span>
            </div>
          ),
        };
      }
    }
  } else if (notification.type === "feedback") {
    if (notification.action === "CREATE") {
      return {
        update: (
          <div>
            <span className="text-gray-400">New </span>
            <Entity type="feedback" color="green" />
            <span className="font-medium text-gray-700">{` ${notification.metadata.feedbackName} `}</span>
            <span className="text-gray-400">created by</span>
            <span className="font-medium text-gray-700">{` ${notification.metadata.createdBy} `}</span>
          </div>
        ),
        type: {
          color: "indigo",
          text: "Feedback",
        },
      };
    } else if (notification.action === "UPDATE") {
      if (notification.subAction === "status change") {
        return {
          update: (
            <div>
              <Entity type="Feedback" color="green" />
              <span className="font-medium text-gray-700">{` ${notification.metadata.feedbackName} `}</span>
              <span className="text-gray-400">updated to</span>
              <span className="font-medium text-gray-700">{` ${notification.metadata.status}`}</span>
            </div>
          ),
        };
      }
    }
  } else if (notification.type === "shared space invite") {
    if (notification.action === "UPDATE") {
      if (notification.subAction === "accepted") {
        return {
          update: (
            <div>
              <span className="text-gray-700">{`${notification.metadata.userName} `}</span>
              <span className="text-gray-400">
                logged in for the first time
              </span>
            </div>
          ),
        };
      }
    }
  } else if (notification.type === "integration") {
    if (notification.action === "CRM_SYNC_COMPLETE") {
      return {
        update: (
          <div>
            <span className="text-gray-700">{`${
              notification.metadata.integrationId === "hubspot"
                ? "HubSpot"
                : "Salesforce"
            } `}</span>
            <span className="text-gray-400">data import completed</span>
          </div>
        ),
      };
    }
  } else if (notification.type === "email log") {
    if (notification.action === "UPDATE") {
      if (notification.subAction === "bounced") {
        return {
          update: (
            <div>
              <span className="text-gray-400">Email to</span>
              <span className="text-gray-700">{` ${notification.metadata.toEmail} `}</span>
              <span className="text-gray-400">in the project</span>
              <span className="text-gray-700">{` ${notification.metadata.projectName} `}</span>
              <span className="text-gray-400">has bounced.</span>
            </div>
          ),
        };
      } else if (notification.subAction === "delivery delayed") {
        return {
          update: (
            <div>
              <span className="text-gray-400">Email to</span>
              <span className="text-gray-700">{` ${notification.metadata.toEmail} `}</span>
              <span className="text-gray-400">in the project</span>
              <span className="text-gray-700">{` ${notification.metadata.projectName} `}</span>
              <span className="text-gray-400">has been delayed.</span>
            </div>
          ),
        };
      }
    }
  }
  // else if (notification.type === "shared space invite") {
  //   if (notification.action === "CREATE") {
  //     return {
  //       update: (
  //         <div>
  //           <Entity type="Project Invite" color="pink" />
  //           <span className="font-medium text-gray-400">{` sent to `}</span>
  //           <span className="font-medium text-gray-700">{`${notification.metadata.toEmail}`}</span>
  //         </div>
  //       ),
  //     };
  //   } else if (notification.action === "UPDATE") {
  //     if (notification.subAction === "accepted") {
  //       return {
  //         update: (
  //           <div>
  //             <Entity type="Project Invite" color="pink" />
  //             <span className="font-medium text-gray-400">{` accepted by `}</span>
  //             <span className="font-medium text-gray-700">{`${notification.metadata.userName}`}</span>
  //           </div>
  //         ),
  //       };
  //     }
  //   }
  // } else if (notification.type === "instance invite") {
  //   if (notification.action === "CREATE") {
  //     return {
  //       update: (
  //         <div>
  //           <Entity type="Team Invite" color="pink" />
  //           <span className="font-medium text-gray-400">{` sent to `}</span>
  //           <span className="font-medium text-gray-700">{`${notification.metadata.toEmail}`}</span>
  //         </div>
  //       ),
  //     };
  //   }
  //   else if (notification.action === "UPDATE") {
  //     if (notification.subAction === "accepted") {
  //       return {
  //         update: (
  //           <div>
  //             <Entity type="Team Invite" color="pink" />
  //             <span className="font-medium text-gray-400">{` accepted by `}</span>
  //             <span className="font-medium text-gray-700">{`${notification.metadata.userName}`}</span>
  //           </div>
  //         ),
  //       };
  //     }
  //   }
  // }
  return { update: null };
}

export function Entity({
  type,
  color,
}: {
  type: string;
  color: "green" | "blue" | "gray" | "red" | "purple" | "pink" | "yellow";
}) {
  return (
    <span
      className={clsx(
        "rounded px-1.5 py-[3px] text-xs",
        color === "green" && "bg-green-50 text-green-600",
        color === "blue" && "bg-blue-50 text-blue-600",
        color === "red" && "bg-red-50 text-red-600",
        color === "gray" && "bg-gray-50 text-gray-600",
        color === "purple" && "bg-purple-50 text-purple-600",
        color === "pink" && "bg-pink-50 text-pink-600",
        color === "yellow" && "bg-yellow-50 text-yellow-600"
      )}
    >
      {type}
    </span>
  );
}
